import React, { useEffect, useMemo, useRef } from "react";
import { Container, Sprite, Text } from "@pixi/react";
import { TextStyle } from "pixi.js";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import anime from "animejs";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { getNewUITexture } from "../../../../assets";
import { PixiSettings } from "../../../../pixiSettings";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
} from "../../../../pixiSettings";
import { outlineShadow } from "../../../../pixiSettings";
import { selectReplaySessionActive } from "../../../../State/selectors";

// Small red "recording" dot next to the "REPLAY" text (docs/replay-history-spec.md
// §5 — the replay indicator badge). Only shown while a replay round is actively
// playing — gated on selectReplaySessionActive (distinct from the panel/modal
// visibility flags, which just track which popup is open).
//
// Position below is a first-pass placeholder next to Clock's slot — design
// assets for this are still pending, so exact placement will need a visual
// pass once those land. Text style is intentionally copied verbatim from
// BalanceDisplay's amount text (fontWeight, fill, drop-shadow, resolution).
// Both relative to the text font size, so the whole badge scales as one
// unit — shrink the font and the dot + gap shrink with it, keeping the same
// look/ratio at every size instead of a fixed-px gap looking wrong when tiny.
const DOT_SIZE_RATIO = 0.78;
const DOT_TEXT_GAP_RATIO = 0.16;
// Old camera "recording" dot — mostly on, with a quick blink-off. Not a
// continuous alternating fade (that reads as frantic strobing); instead it
// HOLDS at full opacity, transitions quickly to invisible, HOLDS there
// briefly, then transitions quickly back — same shape as a real REC light.
const PULSE_ON_HOLD = 700; // ms fully visible before the blink
const PULSE_TRANSITION = 100; // ms for each quick fade, on↔off
const PULSE_OFF_HOLD = 300; // ms fully invisible before it comes back

const ReplayIndicator = ({ toolbar }) => {
  const { t } = useTranslation();
  const sessionActive = useSelector(selectReplaySessionActive);
  const { device, orientation, getResponsiveConfig, uiSymbolScale, isMobileSmall } =
    useDimensions();
  const dotTexture = getNewUITexture("icon_dot_replay");

  const dotRef = useRef(null);
  // Stable plain object as the anime target (this codebase's convention for
  // animating PIXI objects — see MiniPaytable.jsx — rather than targeting the
  // ref directly), applied to the real Graphics ref's alpha in `update`.
  const pulseProxy = useRef({ alpha: 1 }).current;

  useEffect(() => {
    if (!sessionActive) return;
    // Each keyframe's `duration` is the time to transition INTO that value —
    // pairing a keyframe with the value it already has (1→1, 0→0) is how a
    // hold is expressed here, not a separate "delay" step.
    const pulse = anime({
      targets: pulseProxy,
      keyframes: [
        { alpha: 1, duration: PULSE_ON_HOLD }, // hold fully visible
        { alpha: 0, duration: PULSE_TRANSITION }, // quick blink off
        { alpha: 0, duration: PULSE_OFF_HOLD }, // hold invisible
        { alpha: 1, duration: PULSE_TRANSITION }, // quick blink back on
      ],
      loop: true,
      easing: "easeInOutSine",
      update: () => {
        if (dotRef.current) dotRef.current.alpha = pulseProxy.alpha;
      },
    });
    return () => pulse.pause();
  }, [sessionActive, pulseProxy]);

  // Reuses the same device-config pattern as Clock/CompanyLogo.
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  const indicatorConfig = useMemo(() => {
    let fontMultiplier = 1.0;
    let x = 0;
    let y = 0;

    if (isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        // Top right corner, above ButtonSound's own mobileSmall-portrait slot.
        fontMultiplier = 1.0;
        x = toolbar.width - buttonConfig.width * 1.5;
        y = buttonConfig.height * 0.46;
      } else {
        // Landscape — same row as CompanyLogo (x = toolbar.width - buttonConfig.width*1.0),
        // sitting to its left.
        fontMultiplier = 0.975;
        x = toolbar.width - buttonConfig.width * 3.45;
        y = buttonConfig.height * 0.7;
      }
    } else if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        // Same row as ButtonSound (y = buttonConfig.height * 1.6 in its own
        // mobile-portrait slot), sitting to its left. x is the dot's center
        // and the text grows rightward from there, so this offset has to
        // clear the whole dot+label before the button's left edge
        // (toolbar.width - buttonConfig.width * 1.35).
        fontMultiplier = 0.9;
        x = toolbar.width - buttonConfig.width * 2.35;
        y = buttonConfig.height * 1.42;
      } else {
        // Landscape — same row as CompanyLogo (x = toolbar.width - buttonConfig.width*0.4),
        // sitting to its left, same treatment as mobileSmall landscape above.
        fontMultiplier = 0.9;
        x = toolbar.width - buttonConfig.width * 2.7;
        y = buttonConfig.height * 0.65;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      // ── Beside ButtonSound, the way every other device does it ──
      //
      // Both orientations used to take `toolbar.width / 2`, which parks the
      // badge in the middle of the toolbar — directly under the Clock, which is
      // centred on tablet too — instead of anchoring it to anything.
      //
      // NO orientation split, deliberately: ButtonSound's tablet slot is the
      // SAME in portrait and landscape,
      //   x = toolbar.width - buttonConfig.width * 0.8
      //   y = buttonConfig.width * 0.8          (width, not height — its convention)
      // so one position tracks it in both. Measured from the RIGHT edge, so a
      // landscape toolbar being far wider changes nothing.
      //
      // ButtonSound is anchored 0.5 and this badge is centred vertically too
      // (dot anchor 0.5, text anchor y 0.5), so copying that `y` puts the two on
      // exactly one row with no nudge.
      //
      // x is the DOT'S CENTRE and the label grows RIGHTWARD from it, so the
      // offset must clear the whole dot + "REPLAY" before the button's left edge
      // at toolbar.width - buttonConfig.width * 1.3. 2.8 was measured and still
      // overlapped; 3.4 clears it. Mobile portrait gets away with 1.5 because it
      // renders at fontMultiplier 0.9 — this branch is 1.2, so the label is a
      // third wider.
      fontMultiplier = 1.2;
      x = toolbar.width - buttonConfig.width * 3.4;
      y = buttonConfig.width * 0.8;
    } else {
      // Desktop — near Clock's slot, one row below it, nudged right to line up with it.
      fontMultiplier = 0.85;
      x = toolbar.width - buttonConfig.width * 3.75;
      y = buttonConfig.height * 1.45;
    }

    return { fontMultiplier, x, y };
  }, [device, orientation, isMobileSmall, toolbar.width, buttonConfig.height, buttonConfig.width]);

  const baseFontSize = 32; // matches BalanceDisplay's value text base size
  const fontSize = useMemo(() => {
    const responsiveFontSize = baseFontSize * indicatorConfig.fontMultiplier;
    const raw = responsiveFontSize * uiSymbolScale;
    return Math.round(raw / 2) * 2; // snap to even for clean rasterization
  }, [indicatorConfig.fontMultiplier, uiSymbolScale]);

  // Text style copied verbatim from BalanceDisplay's amount text — same
  // weight, fill, drop-shadow, resolution. Only fontSize differs (this is a
  // small toolbar readout, not the balance value itself).
  const textStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize,
        fontWeight: "700",
        fill: 0xffffff,
        align: "left",
        resolution: window.devicePixelRatio || 1,
        // ...TEXT_STYLE_CONFIG.OUTLINE,
        ...outlineShadow(fontSize), // shadow scales with the text
      }),
    [fontSize],
  );

  const replayText = useMemo(
    () => t("replayHistoryEndedModal_replay_btn"),
    [t],
  );

  // Square-ish icon (23×24 in the sheet) — scale both sides together off font size.
  const dotSize = useMemo(
    () => Math.max(8, Math.round(fontSize * DOT_SIZE_RATIO)),
    [fontSize],
  );

  const dotTextGap = useMemo(
    () => Math.round(fontSize * DOT_TEXT_GAP_RATIO),
    [fontSize],
  );

  if (!sessionActive) return null;

  return (
    <Container
      name="secondary_replay_indicator"
      x={Math.round(indicatorConfig.x)}
      y={Math.round(indicatorConfig.y)}
      zIndex={3}
    >
      <Sprite
        ref={dotRef}
        name="replay_indicator_dot"
        texture={dotTexture}
        anchor={0.5}
        width={dotSize}
        height={dotSize}
        x={0}
        // The PNG's baked-in drop shadow sits below the dot itself, so the
        // sprite's geometric center (used by anchor 0.5) reads as too high —
        // nudge down a touch to compensate.
        y={dotSize * 0.06}
        roundPixels={true}
      />
      <Text
        text={replayText}
        style={textStyle}
        anchor={{ x: 0, y: 0.5 }}
        x={dotSize / 2 + dotTextGap}
        y={0}
        roundPixels={true}
        accessible={true}
        accessibleTitle={replayText}
      />
    </Container>
  );
};

export default ReplayIndicator;
